import { useEffect, useRef, useState } from 'react'

export type ProposalLoadingStage = 'REVIEWING_PALETTE' | 'SEARCHING_CATALOG' | 'GENERATING_RECOMMENDATIONS'

export interface ProposalLoadingStageConfig {
  stage: ProposalLoadingStage
  headline: string
  progressPercent: number
  startsAtMs: number
  fillDurationMs: number
  creepToPercent: number
  creepDurationMs: number
}

// The last stage stops at 94% on purpose: the bar has to stay visibly unfinished
// until the proposal response actually lands, however long that takes.
// The newlines are the mock's explicit line breaks and only render because
// .proposal-loading-headline sets `white-space: pre-line`.
// A stage held by the backend can last far longer than its own fill: creepTo eases
// on toward — never reaching — the next stage's percent, so the bar keeps moving
// without claiming progress the backend hasn't made. Creep durations are paced
// against each stage's p95 on POST /quiz/submit/last-step; the catalog search is
// the 1.8s-to-22s one, so it creeps the longest.
export const PROPOSAL_LOADING_STAGES: ProposalLoadingStageConfig[] = [
  {
    stage: 'REVIEWING_PALETTE',
    headline: 'Reviewing\nyour palette',
    progressPercent: 24,
    startsAtMs: 0,
    fillDurationMs: 600,
    creepToPercent: 70,
    creepDurationMs: 3000,
  },
  {
    stage: 'SEARCHING_CATALOG',
    headline: 'Searching\ndesign catalog',
    progressPercent: 74,
    startsAtMs: 2800,
    fillDurationMs: 2000,
    creepToPercent: 90,
    creepDurationMs: 12000,
  },
  {
    stage: 'GENERATING_RECOMMENDATIONS',
    headline: 'Generating\nyour recommendations',
    progressPercent: 94,
    startsAtMs: 5600,
    fillDurationMs: 4200,
    creepToPercent: 94,
    creepDurationMs: 4200,
  },
]

export const getProposalLoadingStageConfig = (stage: ProposalLoadingStage): ProposalLoadingStageConfig =>
  PROPOSAL_LOADING_STAGES.find((config) => config.stage === stage) || PROPOSAL_LOADING_STAGES[0]

export const useTimedProposalLoadingStage = (): ProposalLoadingStage => {
  const [stageIndex, setStageIndex] = useState(0)

  useEffect(() => {
    const timers = PROPOSAL_LOADING_STAGES.slice(1).map((config, index) =>
      window.setTimeout(() => setStageIndex(index + 1), config.startsAtMs)
    )

    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [])

  return PROPOSAL_LOADING_STAGES[stageIndex].stage
}

const stageIndexOf = (stage: ProposalLoadingStage) =>
  Math.max(
    PROPOSAL_LOADING_STAGES.findIndex((config) => config.stage === stage),
    0
  )

/**
 * Picks the stage to render, preferring a backend-reported one over the timed
 * schedule. The timed schedule can be ahead of the backend when the first report
 * arrives, so the result is clamped to the furthest stage already shown — the bar
 * must never travel backwards, even when that means briefly overstating progress.
 */
export const useDisplayedProposalLoadingStage = (backendStage?: ProposalLoadingStage): ProposalLoadingStage => {
  const timedStage = useTimedProposalLoadingStage()
  const furthestShownIndex = useRef(0)

  const reportedIndex = stageIndexOf(backendStage || timedStage)
  const displayedIndex = backendStage ? Math.max(reportedIndex, furthestShownIndex.current) : reportedIndex

  furthestShownIndex.current = Math.max(furthestShownIndex.current, displayedIndex)

  return PROPOSAL_LOADING_STAGES[displayedIndex].stage
}
